import { Spacing } from '@persona-id/design-tokens';
import { useState } from 'react';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import Permissioned from 'dashboard/components/Permissioned';
import { ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import TableLayout, {
  TableNullState,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import { userDisplayName } from 'dashboard/lib/valueDisplayers/userDisplayName';
import { User } from 'dashboard/models/user';
import AddUsersList from 'dashboard/views/DashboardTeamShowView/AddUsersList';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Checkbox from 'design-system/components/Checkbox';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import Sticker from 'design-system/components/Sticker';
import { Span } from 'design-system/components/Text';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';

const TableTitleContainer = styled('div')`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-column-gap: ${Spacing[2]};
`;

const RoleStickersContainer = styled('div')`
  display: flex;
  grid-gap: ${Spacing[2]};
  flex-wrap: wrap;
`;

const StickyModalHeader = styled(ModalHeader)`
  top: 0;
  position: sticky;
  background: ${({ theme }) => theme.color.background.primary};
`;

const StyledModalContainer = styled(ModalContainer)`
  overflow: hidden;
`;

export interface MemberListPanelProps {
  users: User[];
  teamUserIds: string[];
  userIdToRoleNames: Record<string, string[]>;
  setUserIds?: (userIds: string[]) => void;
}

const MemberListPanel = ({
  users,
  teamUserIds,
  userIdToRoleNames,
  setUserIds,
}: MemberListPanelProps) => {
  const [addUsersModal, { enable: showAddUsersModal, disable: hideAddUsersModal }] =
    useToggle(false);

  const clearedUserIdMap = teamUserIds.reduce(
    (acc, id) => {
      return {
        ...acc,
        [id]: false,
      };
    },
    {} as Record<string, boolean>,
  );

  const canEditTeam = usePermission(Permission.TeamWrite);

  const [selectedUserIdMap, setSelectedUserIdMap] = useState(clearedUserIdMap);
  const selectedUserCount = Object.values(selectedUserIdMap).reduce(
    (sum, isChecked) => (isChecked ? sum + 1 : sum),
    0,
  );

  const teamUsers = users.filter((user) => teamUserIds.includes(user.id));
  const activeUsersNotInTeam = users.filter(
    (user) => !teamUserIds.includes(user.id) && user.attributes.status === 'active',
  );

  const handleAddUsers = (userIds: string[]) => {
    const nextUserIds = [...teamUserIds, ...userIds];
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setUserIds(nextUserIds);
    hideAddUsersModal();
  };

  // TODO [AMY]: show visual indicator for inactive users who should be removed from a team
  const userRows = teamUsers.map((user) => {
    const checked = selectedUserIdMap[user.id];
    const roleNames = userIdToRoleNames[user.id];
    return (
      <TableTR
        id={`member-list-panel__user-row-${user.id}`}
        key={user.id}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        onClick={
          canEditTeam
            ? () => setSelectedUserIdMap({ ...selectedUserIdMap, [user.id]: !checked })
            : null
        }
      >
        <Permissioned permission={Permission.TeamWrite}>
          <TableTD>
            <Checkbox checked={checked} />
          </TableTD>
        </Permissioned>
        <TableTD>{userDisplayName(user)}</TableTD>
        <TableTD>{user.attributes['email-address']}</TableTD>
        <TableTD>
          <RoleStickersContainer>
            {roleNames.map((roleName) => {
              return roleName != null ? (
                <Sticker
                  variant='primary'
                  key={`team-member__role-sticker--${user.id}-${roleName}`}
                >
                  {roleName}
                </Sticker>
              ) : (
                <Sticker key={`team-member__role-sticker--${user.id}-${roleName}`}>
                  Restricted
                </Sticker>
              );
            })}
          </RoleStickersContainer>
        </TableTD>
      </TableTR>
    );
  });

  return (
    <>
      <TableTitleContainer>
        <Card.Title>Members</Card.Title>
        <Permissioned permission={Permission.TeamWrite}>
          <HStack>
            <Button
              id='team-table__remove-users'
              variant='basic'
              disabled={selectedUserCount === 0}
              onClick={() => {
                const nextUserIds = teamUserIds.filter((userId) => !selectedUserIdMap[userId]);
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                setUserIds(nextUserIds);
                setSelectedUserIdMap(clearedUserIdMap);
              }}
            >
              Remove {selectedUserCount > 0 && `(${selectedUserCount})`}
            </Button>
            <Button
              id='team-table__add-users-button'
              variant='basic'
              disabled={activeUsersNotInTeam.length === 0}
              onClick={showAddUsersModal}
            >
              Add user
            </Button>
          </HStack>
        </Permissioned>
      </TableTitleContainer>
      <TableLayout
        showHeader={true}
        isEmpty={teamUsers.length === 0}
        headerTR={
          <>
            <Permissioned permission={Permission.TeamWrite}>
              <TableTH>{/* Checkbox */}</TableTH>
            </Permissioned>
            <TableTH>Name</TableTH>
            <TableTH>Email</TableTH>
            <TableTH>Roles</TableTH>
          </>
        }
        nullState={
          <TableNullState>
            <Permissioned
              permission={Permission.TeamWrite}
              fallback={<Span>This team doesn&apos;t have any members.</Span>}
            >
              <Span>This team doesn&apos;t have any members. Add users to get started.</Span>
              <Spacer height={2} />
              <Button
                id='team-table-null-state__add-users-button'
                width='button'
                variant='basic'
                disabled={activeUsersNotInTeam.length === 0}
                onClick={showAddUsersModal}
              >
                Add user
              </Button>
            </Permissioned>
          </TableNullState>
        }
      >
        {userRows}
      </TableLayout>
      <ModalWrapper
        isOpen={addUsersModal}
        handleCloseModal={hideAddUsersModal}
        styleOverride={{
          content: {
            // Height must be specified on component with overflow auto for child sticky components to work
            height: '100%',
            // Limits the height of the modal so we can have scroll and sticky behaviour within the modal
            maxHeight: '90%',
          },
        }}
      >
        <StyledModalContainer size='lg'>
          <StickyModalHeader onClose={hideAddUsersModal}>Add users</StickyModalHeader>

          <AddUsersList
            activeUsers={activeUsersNotInTeam}
            userIdToRoleNames={userIdToRoleNames}
            addUsers={handleAddUsers}
            handleCancel={hideAddUsersModal}
          />
        </StyledModalContainer>
      </ModalWrapper>
    </>
  );
};

export default MemberListPanel;
